/**
 * DetailsPage (booking FORM page) styles — CWR 2.5.
 *
 * Ports the booking-form-page mockup onto the INJECTED per-tenant client tokens
 * (never the mockup's literal :root hex). Structure mirrors the 2.4 detail page:
 * a fixed page, an inner scroller, a glass Back, a booking hero (BookingHero,
 * in-flow), a rising sheet, and an INLINE bottom CTA at the end of the form (not a
 * fixed footer — the keyboard can't push it up). The dark scrim + the neutral grab
 * handle are image-space (sanctioned literals, same as 2.4).
 */

/* ── Page + scroll (fullscreen adaptation of the mockup .iphone-frame / .app) ── */
.page {
  position: fixed;
  /* Lock to the STABLE (max) viewport height, NOT inset:0 — Telegram shrinks the
     viewport when the keyboard opens, and a viewport-tied root collapses/jumps.
     --app-viewport-stable-height is held at max by useAppViewportCssVars (same rail
     AdminLayout uses). */
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-stable-height, 100dvh);
  max-width: 480px;
  margin: 0 auto;
  background: var(--bg-page);
  color: var(--text-primary);
  overflow: hidden;
  font-family: var(--font-body);
}
.scroll {
  position: absolute;
  /* SHRINK the scroll viewport to the keyboard-visible height (--app-viewport-height,
     reactive) rather than lengthening content with keyboard padding. The scroller's
     bottom then sits at the keyboard top, so its geometry is stable while the keyboard
     animates and a bottom field scrolls up naturally. .page stays at stable full height. */
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-viewport-height, 100dvh);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 24px);
  overscroll-behavior: none;
  /* Stop WebKit scroll anchoring from re-adjusting scrollTop on keyboard layout
     changes — it fights the keyboard-aware scroll. (Same as AdminLayout .content.) */
  overflow-anchor: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}
/* Transparent window in the scroller revealing the pinned hero underneath (= hero height). */
.heroSpacer {
  height: 330px;
  flex-shrink: 0;
  pointer-events: none;
}

/* ── Back button — glass, at the catalog/detail screen spot (clears Telegram chrome) ── */
/* rd-bookings — positioning only; the glass square + arrow are owned by the
   shared FlowBackButton (design D6). */
.back {
  position: fixed;
  top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) + 12px
  );
  left: 18px;
  z-index: 80;
}

/* ── Rising sheet — scrolls UP over the pinned hero (matches the 2.4 detail page) ── */
.sheet {
  position: relative;
  z-index: 1;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: -4px auto 0;
}
.body {
  padding: 16px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Editorial form header + form-count dots ── */
.fhead {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 13px;
  margin-bottom: 1px;
  border-bottom: 1px solid var(--border-subtle);
}
.fheadTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.ttl {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.fname {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 21px;
  letter-spacing: -0.012em;
  line-height: 1.1;
  color: var(--text-primary);
  margin: 0;
}
.req {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--accent);
}
.fcount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 4px;
}
.dots {
  display: flex;
  gap: 5px;
}
.dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.22);
  transition: background 0.2s ease;
}
.dots i.dotOn {
  background: var(--accent);
  box-shadow: 0 0 8px rgba(var(--accent-rgb), 0.5);
}
.countLbl {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-tertiary);
  white-space: nowrap;
}
.fdesc {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ── Reserved-slot line ── */
.reservedInfo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  padding: 7px 11px;
  align-self: flex-start;
  background: rgba(var(--accent-rgb), 0.1);
  border-radius: 10px;
  font-size: 12.5px;
  color: var(--accent);
}

/* ── Bottom CTA — INLINE, the last item in the form's scroll stack (rendered via
   renderActions). Not fixed/sticky: it flows after the fields so the keyboard never
   pushes it up; the user scrolls to it like any other content. ── */
.formbar {
  margin-top: 8px;
}
.btnPrimary {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber, 0 0 32px rgba(var(--accent-rgb), 0.28));
}
.btnPrimary:disabled {
  opacity: 0.6;
}
.btnPrimary svg {
  width: 17px;
  height: 17px;
  stroke-width: 2.4;
}

/* ── Loading + error states (themed; no ProgressBar) ── */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  gap: 16px;
  color: var(--text-secondary);
}
.loading p {
  margin: 0;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--glass-bg-strong);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 28px;
  text-align: center;
}
.error h2 {
  font-family: var(--font-display);
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}
.error p {
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  max-width: 280px;
}
.error button {
  padding: 12px 24px;
  background: var(--accent);
  color: var(--text-on-accent);
  border: none;
  border-radius: 14px;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
